/**
 * Servicio para gestión del módulo de Producción
 * Incluye: Check List Ensamble, Órdenes de Trabajo y Liberaciones
 */

import api from './api.js';

class ProduccionService {
  // =====================================================
  // CHECK LIST ENSAMBLE (AMX-PR-FR-02)
  // =====================================================

  /**
   * Listar checklists con filtros
   */
  async listarChecklists(params = {}) {
    const cleanParams = this.limpiarParams(params);
    return await api.get('/produccion/checklists', cleanParams);
  }

  /**
   * Obtener checklist por ID
   */
  async obtenerChecklist(id) {
    return await api.get(`/produccion/checklists/${id}`);
  }

  /**
   * Obtener checklist por número de serie del tractor
   */
  async obtenerChecklistPorTractor(numSerie) {
    return await api.get(`/produccion/checklists/tractor/${numSerie}`);
  }

  /**
   * Lookup previo a crear checklist. Devuelve { puede_crear, code, mensaje, tractor }.
   * Si puede_crear=false, el frontend debe mostrar el mensaje y NO dejar avanzar.
   */
  async lookupParaChecklist(numSerie) {
    return await api.get(`/produccion/checklists/lookup/${encodeURIComponent(numSerie)}`);
  }

  /**
   * Crear nuevo checklist
   */
  async crearChecklist(data) {
    return await api.post('/produccion/checklists', data);
  }

  /**
   * Actualizar checklist
   */
  async actualizarChecklist(id, data) {
    return await api.put(`/produccion/checklists/${id}`, data);
  }

  /**
   * Agregar item al checklist
   */
  async agregarItemChecklist(checklistId, itemData) {
    return await api.post(`/produccion/checklists/${checklistId}/items`, itemData);
  }

  /**
   * Actualizar item del checklist
   */
  async actualizarItemChecklist(itemId, data) {
    const response = await fetch(`${api.baseUrl}/produccion/checklists/items/${itemId}`, {
      method: 'PATCH',
      headers: api.getHeaders(),
      body: JSON.stringify(data)
    });
    return await api.handleResponse(response);
  }

  /**
   * Verificar múltiples items
   */
  async verificarItemsChecklist(checklistId, items) {
    return await api.post(`/produccion/checklists/${checklistId}/verificar`, { items });
  }

  /**
   * Completar checklist con firmas
   */
  async completarChecklist(id, data) {
    return await api.post(`/produccion/checklists/${id}/completar`, data);
  }

  /**
   * Registrar niveles de fluidos del checklist
   */
  async registrarNivelesChecklist(checklistId, data) {
    return await api.post(`/produccion/checklists/${checklistId}/niveles`, data);
  }

  /**
   * Registrar torques (Nm/PSI) del checklist
   */
  async registrarTorquesChecklist(checklistId, data) {
    return await api.post(`/produccion/checklists/${checklistId}/torques`, data);
  }

  /**
   * Guardar todos los cambios del checklist (items, niveles, llantas, observaciones)
   */
  async guardarCambiosChecklist(id, data) {
    return await api.put(`/produccion/checklists/${id}/guardar-cambios`, data);
  }

  /**
   * Eliminar checklist
   */
  async eliminarChecklist(id) {
    return await api.delete(`/produccion/checklists/${id}`);
  }

  /**
   * Obtener estadísticas de checklists
   */
  async obtenerEstadisticasChecklists() {
    return await api.get('/produccion/checklists/estadisticas');
  }

  /**
   * Obtener plantilla de items estándar
   */
  async obtenerPlantillaChecklist() {
    return await api.get('/produccion/checklists/plantilla');
  }

  // =====================================================
  // ÓRDENES DE TRABAJO (AMX-PR-FR-01)
  // =====================================================

  /**
   * Listar órdenes de trabajo con filtros
   */
  async listarOrdenesTrabajo(params = {}) {
    const cleanParams = this.limpiarParams(params);
    return await api.get('/produccion/ordenes-trabajo', cleanParams);
  }

  /**
   * Obtener orden de trabajo por ID
   */
  async obtenerOrdenTrabajo(id) {
    return await api.get(`/produccion/ordenes-trabajo/${id}`);
  }

  /**
   * Listar órdenes de trabajo por tractor
   */
  async listarOrdenesPorTractor(numSerie) {
    const encoded = encodeURIComponent(String(numSerie ?? ''));
    return await api.get(`/produccion/ordenes-trabajo/tractor/${encoded}`);
  }

  /**
   * Crear nueva orden de trabajo
   */
  async crearOrdenTrabajo(data) {
    return await api.post('/produccion/ordenes-trabajo', data);
  }

  /**
   * Actualizar orden de trabajo
   */
  async actualizarOrdenTrabajo(id, data) {
    return await api.put(`/produccion/ordenes-trabajo/${id}`, data);
  }

  /**
   * Registrar niveles de fluidos
   */
  async registrarNivelesOrden(ordenId, niveles) {
    return await api.post(`/produccion/ordenes-trabajo/${ordenId}/niveles`, niveles);
  }

  /**
   * Agregar item de mantenimiento
   */
  async agregarItemMantenimiento(ordenId, itemData) {
    return await api.post(`/produccion/ordenes-trabajo/${ordenId}/items`, itemData);
  }

  /**
   * Actualizar item de mantenimiento
   */
  async actualizarItemMantenimiento(ordenId, itemId, data) {
    const response = await fetch(`${api.baseUrl}/produccion/ordenes-trabajo/${ordenId}/items/${itemId}`, {
      method: 'PATCH',
      headers: api.getHeaders(),
      body: JSON.stringify(data)
    });
    return await api.handleResponse(response);
  }

  /**
   * Agregar diagnóstico
   */
  async agregarDiagnostico(ordenId, diagnostico) {
    return await api.post(`/produccion/ordenes-trabajo/${ordenId}/diagnosticos`, diagnostico);
  }

  /**
   * Eliminar diagnóstico
   */
  async eliminarDiagnostico(ordenId, diagId) {
    return await api.delete(`/produccion/ordenes-trabajo/${ordenId}/diagnosticos/${diagId}`);
  }

  /**
   * Guardar todos los cambios de una orden de trabajo
   */
  async guardarCambiosOT(ordenId, data) {
    return await api.put(`/produccion/ordenes-trabajo/${ordenId}/guardar-cambios`, data);
  }

  /**
   * Actualizar inspección visual de orden de trabajo
   */
  async actualizarInspeccionVisual(ordenId, data) {
    return await api.put(`/produccion/ordenes-trabajo/${ordenId}/inspeccion-visual`, data);
  }

  /**
   * Cambiar estado de orden
   */
  async cambiarEstadoOrden(ordenId, estado, observaciones = null) {
    const response = await fetch(`${api.baseUrl}/produccion/ordenes-trabajo/${ordenId}/estado`, {
      method: 'PATCH',
      headers: api.getHeaders(),
      body: JSON.stringify({ estado, observaciones })
    });
    return await api.handleResponse(response);
  }

  /**
   * Completar orden de trabajo con firmas
   */
  async completarOrdenTrabajo(id, data) {
    return await api.post(`/produccion/ordenes-trabajo/${id}/completar`, data);
  }

  /**
   * Eliminar orden de trabajo
   */
  async eliminarOrdenTrabajo(id) {
    return await api.delete(`/produccion/ordenes-trabajo/${id}`);
  }

  /**
   * Obtener estadísticas de órdenes
   */
  async obtenerEstadisticasOrdenes() {
    return await api.get('/produccion/ordenes-trabajo/estadisticas');
  }

  /**
   * Obtener plantilla de items de mantenimiento
   */
  async obtenerPlantillaMantenimiento() {
    return await api.get('/produccion/ordenes-trabajo/plantilla');
  }

  /**
   * Obtener plantilla completa de mantenimiento (todos los items con modelos)
   */
  async obtenerPlantillaCompleta() {
    return await api.get('/produccion/ordenes-trabajo/plantilla-completa');
  }

  /**
   * Intervalo de checklist: con num_serie usa OT completadas de ese tractor; sin num_serie, solo horas motor.
   */
  async calcularIntervaloMantenimientoOT(horas, numSerie = null) {
    const params = numSerie ? { num_serie: numSerie } : {};
    return await api.get(`/produccion/ordenes-trabajo/calcular-intervalo/${horas}`, params);
  }

  // =====================================================
  // LIBERACIÓN DE TRACTOR (AMX-PR-FR-03)
  // =====================================================

  /**
   * Listar liberaciones con filtros
   */
  async listarLiberaciones(params = {}) {
    const cleanParams = this.limpiarParams(params);
    return await api.get('/produccion/liberaciones', cleanParams);
  }

  /**
   * Obtener liberación por ID
   */
  async obtenerLiberacion(id) {
    return await api.get(`/produccion/liberaciones/${id}`);
  }

  /**
   * Obtener liberación por número de serie del tractor
   */
  async obtenerLiberacionPorTractor(numSerie) {
    return await api.get(`/produccion/liberaciones/tractor/${numSerie}`);
  }

  /**
   * Verificar si un tractor puede ser liberado
   */
  async verificarPuedeLiberarse(numSerie) {
    return await api.get(`/produccion/liberaciones/verificar/${numSerie}`);
  }

  /**
   * Crear nueva liberación
   */
  async crearLiberacion(data) {
    return await api.post('/produccion/liberaciones', data);
  }

  /**
   * Actualizar liberación
   */
  async actualizarLiberacion(id, data) {
    return await api.put(`/produccion/liberaciones/${id}`, data);
  }

  /**
   * Completar liberación
   */
  async completarLiberacion(id, data = {}) {
    const response = await fetch(`${api.baseUrl}/produccion/liberaciones/${id}/completar`, {
      method: 'PATCH',
      headers: api.getHeaders(),
      body: JSON.stringify(data)
    });
    return await api.handleResponse(response);
  }

  /**
   * Agregar parte certificada
   */
  async agregarParteCertificada(liberacionId, parteData) {
    return await api.post(`/produccion/liberaciones/${liberacionId}/partes`, parteData);
  }

  /**
   * Actualizar parte certificada
   */
  async actualizarParteCertificada(parteId, data) {
    const response = await fetch(`${api.baseUrl}/produccion/liberaciones/partes/${parteId}`, {
      method: 'PATCH',
      headers: api.getHeaders(),
      body: JSON.stringify(data)
    });
    return await api.handleResponse(response);
  }

  /**
   * Verificar múltiples partes
   */
  async verificarPartes(liberacionId, partes) {
    return await api.post(`/produccion/liberaciones/${liberacionId}/verificar-partes`, { partes });
  }

  /**
   * Eliminar liberación
   */
  async eliminarLiberacion(id) {
    return await api.delete(`/produccion/liberaciones/${id}`);
  }

  /**
   * Obtener estadísticas de liberaciones
   */
  async obtenerEstadisticasLiberaciones() {
    return await api.get('/produccion/liberaciones/estadisticas');
  }

  /**
   * Obtener partes certificadas por modelo (plantilla)
   */
  async obtenerPartesModelo(modelo) {
    return await api.get(`/produccion/liberaciones/partes-modelo/${encodeURIComponent(modelo)}`);
  }

  // =====================================================
  // VALIDACIÓN GARANTÍA
  // =====================================================

  /**
   * Validar si aplica garantía para un tractor
   */
  async validarGarantia(numSerie, horasMotor) {
    return api.get(`/produccion/ordenes-trabajo/validar-garantia/${numSerie}?horas_motor=${horasMotor}`);
  }

  // =====================================================
  // FOTOS INSPECCIÓN VISUAL
  // =====================================================

  /**
   * Subir foto de inspección visual
   * Si se pasa numSerie, usa el bucket unificado: ot/{numSerie}/fotos/
   */
  async subirFotoInspeccion(file, numSerie, modulo = 'ot') {
    const formData = new FormData();
    formData.append('foto', file);
    if (numSerie) {
      formData.append('modulo', modulo);
      formData.append('num_serie', numSerie);
      formData.append('tipo', 'fotos');
    }
    const response = await api.upload('/files/foto', formData);
    return response.data;
  }

  // =====================================================
  // ELIMINAR ARCHIVOS
  // =====================================================

  /**
   * Eliminar archivo de GCS por URL
   */
  async eliminarArchivo(fileUrl) {
    return api.post('/files/eliminar', { url: fileUrl });
  }

  // =====================================================
  // UTILIDADES
  // =====================================================

  /**
   * Limpiar parámetros vacíos
   */
  limpiarParams(params) {
    const cleanParams = {};
    Object.keys(params).forEach(key => {
      if (params[key] !== '' && params[key] !== null && params[key] !== undefined) {
        cleanParams[key] = params[key];
      }
    });
    return cleanParams;
  }
}

export default new ProduccionService();
